import React, { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Save, Calendar, DollarSign, AlertCircle } from 'lucide-react'
import { toast } from 'sonner'
import { projectsService, ProjectCreate } from '../services/projects'
import { getErrorMessage } from '../utils/apiTransformers'
import { useLocalization } from '../contexts/LocalizationContext'
import { useTranslation } from '../i18n/I18nContext'
import { useStatusOptions } from '../hooks/useStatusOptions'
import type { ApiErrorLike } from '../utils/apiError'

export default function CreateProject() {
  const navigate = useNavigate()
  const { prefs } = useLocalization()
  const { t } = useTranslation()
  // Part 16 — pull status options from the org's set (or canonical fallback).
  const { options: statusOptions } = useStatusOptions('project')
  const [searchParams] = useSearchParams()
  const portfolioIdFromUrl = searchParams.get('portfolio_id')
  const [loading, setLoading] = useState(false)
  
  // Safely parse portfolio_id from URL
  let portfolioId: number | undefined = undefined
  if (portfolioIdFromUrl) {
    const parsed = parseInt(portfolioIdFromUrl)
    if (!isNaN(parsed)) {
      portfolioId = parsed
    }
  }
  
  const [formData, setFormData] = useState<ProjectCreate>({
    name: '',
    description: '',
    status: 'planning',
    priority: 'medium',
    start_date: '',
    end_date: '',
    budget: '',
    portfolio_id: portfolioId,
  })

  const handleChange = (field: keyof ProjectCreate) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const value = e.target.value
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      // Validate required fields
      if (!formData.name.trim()) {
        toast.error('Project name is required')
        setLoading(false)
        return
      }

      // Validate dates
      if (formData.start_date && formData.end_date) {
        if (new Date(formData.end_date) < new Date(formData.start_date)) {
          toast.error('End date must be after start date')
          setLoading(false)
          return
        }
      }

      // Prepare data for API
      const projectData: ProjectCreate = {
        name: formData.name.trim(),
        description: formData.description?.trim() || undefined,
        status: formData.status,
        priority: formData.priority,
        start_date: formData.start_date || undefined,
        end_date: formData.end_date || undefined,
        budget: formData.budget ? formData.budget : undefined,
      }

      const project = await projectsService.create(projectData)
      toast.success('Project created successfully!')
      navigate(`/project/${project.id}`)
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      console.error('Error creating project:', error)
      toast.error(getErrorMessage(error) || 'Failed to create project. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="p-6 space-y-6 bg-[#f0f4f8] min-h-screen">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#102a43] via-[#486581] to-[#102a43] rounded-xl p-6 text-white shadow-lg border border-[#829ab1]/20 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl mb-2">Create New Project</h1>
              <p className="text-[#bcccdc]">Fill in the details below to create a new project</p>
            </div>
            <button
              onClick={() => navigate('/project')}
              className="px-4 py-2 bg-[#486581] hover:bg-[#5a7692] border border-[#829ab1] rounded-lg transition-colors flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Projects
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-xl border-2 border-[#bcccdc] shadow-md p-8">
          <div className="space-y-6">
            {/* Project Name */}
            <div>
              <label htmlFor="cp-name" className="block text-sm font-medium text-[#102a43] mb-2">
                {t('projects.fields.name')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                id="cp-name"
                onChange={handleChange('name')}
                required
                placeholder={t('projects.fields.name.placeholder')}
                className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none text-[#2A3B4C]"
              />
            </div>

            {/* Description */}
            <div>
              <label htmlFor="cp-description" className="block text-sm font-medium text-[#102a43] mb-2">
                {t('projects.fields.description')}
              </label>
              <textarea
                value={formData.description || ''}
                id="cp-description"
                onChange={handleChange('description')}
                placeholder={t('projects.fields.description.placeholder')}
                rows={4}
                className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none resize-none text-[#2A3B4C]"
              />
            </div>

            {/* Status and Priority */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="cp-status" className="block text-sm font-medium text-[#102a43] mb-2">
                  {t('projects.fields.status')} <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.status}
                  id="cp-status"
                onChange={handleChange('status')}
                  required
                  className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none text-[#2A3B4C]"
                >
                  {statusOptions.map((o) => (
                    <option key={o.key} value={o.key}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="cp-priority" className="block text-sm font-medium text-[#102a43] mb-2">
                  {t('projects.fields.priority')} <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.priority}
                  id="cp-priority"
                onChange={handleChange('priority')}
                  required
                  className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none text-[#2A3B4C]"
                >
                  <option value="low">{t('priority.low')}</option>
                  <option value="medium">{t('priority.medium')}</option>
                  <option value="high">{t('priority.high')}</option>
                  <option value="urgent">{t('priority.urgent')}</option>
                </select>
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="cp-start_date" className="block text-sm font-medium text-[#102a43] mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  {t('projects.fields.startDate')}
                </label>
                <input
                  type="date"
                  value={formData.start_date || ''}
                  id="cp-start_date"
                onChange={handleChange('start_date')}
                  className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none text-[#2A3B4C]"
                />
              </div>

              <div>
                <label htmlFor="cp-end_date" className="block text-sm font-medium text-[#102a43] mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  {t('projects.fields.endDate')}
                </label>
                <input
                  type="date"
                  value={formData.end_date || ''}
                  id="cp-end_date"
                onChange={handleChange('end_date')}
                  min={formData.start_date || undefined}
                  className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none text-[#2A3B4C]"
                />
              </div>
            </div>

            {/* Budget */}
            <div>
              <label htmlFor="cp-budget" className="block text-sm font-medium text-[#102a43] mb-2">
                <DollarSign className="w-4 h-4 inline mr-1" />
                Budget ({prefs.currency})
              </label>
              <input
                type="number"
                value={formData.budget || ''}
                id="cp-budget"
                onChange={handleChange('budget')}
                placeholder="0.00"
                step="0.01"
                min="0"
                className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none text-[#2A3B4C]"
              />
            </div>

            {/* Info Banner */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Note:</p>
                  <p>
                    Additional project details such as stakeholders, milestones, and compliance requirements can be
                    added after project creation in the project workspace.
                  </p>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex items-center justify-between pt-6 border-t border-[#f0f4f8]">
              <button
                type="button"
                onClick={() => navigate('/project')}
                className="px-6 py-2 border-2 border-[#bcccdc] text-[#486581] rounded-lg hover:bg-[#f0f4f8] transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !formData.name.trim()}
                className="px-8 py-2 bg-gradient-to-r from-[#DAA520] to-[#FFD700] text-[#102a43] rounded-lg hover:from-[#e58900] hover:to-[#c86600] transition-colors font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#102a43]"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Create Project
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

